import {inject, observer} from "mobx-react";
import React, {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import toast from "react-hot-toast";
import ApprovalRequestTable from "./approval-request-table";
import {fetch} from "../../utils/api-wrapper";
import InvoiceApprovalModal from "./invoice-approval-modal";
import SummaryCard from "../../components/summary-card";
import {formatMoney} from "../../utils/format-money";
import Modal from "../../components/modal";
import AccessControl from "../../utils/access_control";
import {duplicateApprovalMessage} from "../../utils/expense-duplicate-message";
import TypeFilter, {matchesRequestTypes} from "./type-filter";

// Keep approval state aligned with actions performed by other users. This matches
// the dashboard's normal idle refresh rate without creating excessive API traffic.
const APPROVAL_REFRESH_INTERVAL_MS = 10000;

// The amount the approvals table shows for a request.
const requestAmount = (row) => (row?.type === "line_item" ? row?.line_item_total : row?.invoice_total);

const getRowAmount = (row) => {
    const amount = Number(requestAmount(row));
    return Number.isFinite(amount) ? amount : 0;
};

// A request as the approvals table shows it: supplier – #invoice number – amount (XPEN-3614).
// Missing parts are left out; with none left, the request's type names it.
const bulkRequestLabel = (request, translate) => {
    const parts = [
        request?.supplier_name,
        request?.invoice_number ? `#${request.invoice_number}` : null,
        formatMoney(requestAmount(request)),
    ].filter(Boolean);
    if (parts.length > 0) return parts.join(" – ");
    return request?.type === "line_item"
        ? translate("approval_requests_table.line_item", "Line item")
        : translate("approval_requests_table.invoice", "Invoice");
};

const buildBulkApprovalPayloadRequest = (request) => ({
    id: request.id,
    type: request.type,
    invoice_id: request.invoice_id,
    line_item_view_id: request.line_item_view_id ?? null,
    line_item_key: request.line_item_key ?? null,
});

const ApprovalsPage = ({data}) => {
    const canReviewRequests = AccessControl.canReviewApprovals(data);
    const [approvalRequests, setApprovalRequests] = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [deniedRequests, setDeniedRequests] = useState([]);
    const [historyRequests, setHistoryRequests] = useState([]);
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [activeSection, setActiveSection] = useState(
        canReviewRequests ? "pending" : "my_requests"
    );
    const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
    const [selectedRequestIds, setSelectedRequestIds] = useState([]);
    const [typeFilter, setTypeFilter] = useState([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [showBulkDenialModal, setShowBulkDenialModal] = useState(false);
    const [bulkDenialReason, setBulkDenialReason] = useState("");
    const [bulkDenialError, setBulkDenialError] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    // XPEN-3777: the review page saves the expense, then brings the person back to this denied line to resend it.
    // The ":history:..." suffix is left out, as in the refused-expense email: the link
    // reopens the line whatever number its refusal has by then.
    const openExpenseReview = ({accountId, checksum, requestId}) => {
        const lineRequestId = String(requestId).replace(/:history:.*$/, '');
        const returnTo = `/approvals?section=denied_requests&request_id=${encodeURIComponent(lineRequestId)}`;
        navigate(`/expenses_account/${accountId}/${checksum}/review?return_to=${encodeURIComponent(returnTo)}`);
    };
    const translate = (key, fallback) => data?.t?.read?.(key) || fallback;

    const {pendingCount, pendingTotal} = useMemo(() => {
        const total = approvalRequests.reduce((sum, row) => sum + getRowAmount(row), 0);
        return {pendingCount: approvalRequests.length, pendingTotal: total};
    }, [approvalRequests]);

    const clearRequestIdQueryParam = () => {
        const params = new URLSearchParams(location.search);
        if (!params.get("request_id") && !params.get("section")) {
            return;
        }

        params.delete("request_id");
        params.delete("token");
        params.delete("section");
        const search = params.toString();
        navigate(
            {
                pathname: location.pathname,
                search: search ? `?${search}` : "",
            },
            {replace: true}
        );
    };
    
    const fetchApprovalRequests = (callback = null) => {
        fetch('/approvals/pending', "GET", {}, response => {
            const requests = response.data.requests || [];
            setApprovalRequests(requests);
            if (callback) callback(requests);
        }, () => {
            setApprovalRequests([]);
        });
    }

    const fetchHistoryRequests = () => {
        fetch('/approvals/history', "GET", {}, response => {
            setHistoryRequests(response.data.requests || []);
        }, () => {
            setHistoryRequests([]);
        });
    };

    // Approved reflects current state; History continues to show previous decisions.
    const fetchApprovedRequests = () => {
        fetch('/approvals/approved', "GET", {}, response => {
            setApprovedRequests(response.data.requests || []);
        }, () => { setApprovedRequests([]); });
    };

    const fetchMyRequests = (callback = null) => {
        fetch('/approvals/my-requests', "GET", {}, response => {
            const requests = response.data.requests || [];
            setMyRequests(requests);
            if (callback) callback(requests);
        }, () => {
            setMyRequests([]);
        });
    };

    const fetchDeniedRequests = (callback = null) => {
        fetch('/approvals/my-denied-requests', "GET", {}, response => {
            const requests = response.data.requests || [];
            setDeniedRequests(requests);
            if (callback) callback(requests);
        }, () => {
            setDeniedRequests([]);
        });
    };

    const selectSection = (section) => {
        setActiveSection(section);
        setSelectedApprovalRequest(null);
        setSelectedRequestIds([]);
        if (section === "approved_requests") {
            fetchApprovedRequests();
        } else if (section === "history") {
            fetchHistoryRequests();
        } else if (section === "my_requests") {
            fetchMyRequests();
        } else if (section === "denied_requests") {
            fetchDeniedRequests();
        }
    }

    const handleModalClose = (mustRefresh) => {
        if (!mustRefresh) {
            setSelectedApprovalRequest(null);
            return;
        }

        fetchApprovalRequests((requests) => {
            const nextRequest = requests.find((request) => matchesRequestTypes(request, typeFilter)) || null;
            setSelectedApprovalRequest(nextRequest);
        });
    };

    const refreshApprovalRequests = () => {
        fetchApprovalRequests();
    };

    const shownApprovalRequests = useMemo(
        () => approvalRequests.filter((request) => matchesRequestTypes(request, typeFilter)),
        [approvalRequests, typeFilter]
    );

    const selectedApprovalRequests = useMemo(() => {
        const selectedIds = new Set(selectedRequestIds);
        return approvalRequests.filter((request) => selectedIds.has(request.id));
    }, [approvalRequests, selectedRequestIds]);

    const selectedCount = selectedApprovalRequests.length;

    const updateSelectedRequest = (requestId, checked) => {
        setSelectedRequestIds((current) => {
            if (checked) {
                return current.includes(requestId) ? current : [...current, requestId];
            }
            return current.filter((id) => id !== requestId);
        });
    };

    const selectAllLoadedRequests = (checked) => {
        setSelectedRequestIds(checked ? shownApprovalRequests.map((request) => request.id) : []);
    };

    const clearSelection = () => {
        setSelectedRequestIds([]);
    };

    const bulkSummaryMessage = (result) => (
        `${result.success_count || 0} ${translate("approval_page.bulk_success_count", "completed")}, `
        + `${result.error_count || 0} ${translate("approval_page.bulk_error_count", "failed")}, `
        + `${result.skipped_count || 0} ${translate("approval_page.bulk_skipped_count", "skipped")}`
    );

    // Each line the server refused because its receipt is already in another expense
    // account, with the table's label for it. `submitted` is the list that was sent: a
    // result row's index points into it, whatever the page has refreshed since. A row
    // matching nothing sent is named by its own type only (it carries no label fields).
    const bulkDuplicateLines = (result, submitted) => (Array.isArray(result.results) ? result.results : [])
        .map((row) => {
            const message = duplicateApprovalMessage(data, row, "approver");
            return message && {
                key: row.index,
                label: bulkRequestLabel(submitted[row.index] ?? row, translate),
                message,
            };
        })
        .filter(Boolean);

    const handleBulkActionResponse = (response, submitted) => {
        const result = response?.data || {};
        const message = bulkSummaryMessage(result);
        if ((result.error_count || 0) > 0 || (result.skipped_count || 0) > 0) {
            toast.error(message);
        } else {
            toast.success(message);
        }
        const duplicateLines = bulkDuplicateLines(result, submitted);
        if (duplicateLines.length > 0) {
            toast.error(
                <div>
                    <div>{translate("approval_page.error_bulk_duplicates", "Lines refused as duplicates:")}</div>
                    <ul className="mb-0 pl-3">
                        {duplicateLines.map(({key, label, message: text}) => (
                            <li key={key}>{label}: {text}</li>
                        ))}
                    </ul>
                </div>,
                {duration: 10000}
            );
        }
        clearSelection();
        setShowBulkDenialModal(false);
        setBulkDenialReason("");
        setBulkDenialError(null);
        fetchApprovalRequests();
    };

    const submitBulkAction = (action, cause = null) => {
        if (bulkProcessing || selectedApprovalRequests.length === 0) {
            return;
        }

        setBulkProcessing(true);
        const submitted = selectedApprovalRequests;
        const payload = {
            action,
            requests: submitted.map(buildBulkApprovalPayloadRequest),
        };
        if (cause) {
            payload.cause = cause;
        }

        fetch('/approvals/bulk', "POST", payload, response => {
            setBulkProcessing(false);
            handleBulkActionResponse(response, submitted);
        }, () => {
            setBulkProcessing(false);
            toast.error(translate(
                "approval_page.bulk_action_error",
                "Bulk approval action could not be completed."
            ));
        });
    };

    const openBulkDenialModal = () => {
        setBulkDenialReason("");
        setBulkDenialError(null);
        setShowBulkDenialModal(true);
    };

    const confirmBulkDenial = () => {
        const trimmed = bulkDenialReason.trim();
        if (!trimmed) {
            setBulkDenialError(translate(
                "approval_modal.deny_reason_required",
                "A reason is required to deny this request."
            ));
            return false;
        }
        submitBulkAction("deny", trimmed);
    };

    // The refused-expense email (XPEN-3585) links to the Refused section, and to
    // one refused item when it is about one. Refused rows carry a ":history:..."
    // suffix (a number or "fallback") the link cannot know, so the item is matched
    // by prefix; the trailing colon keeps item 1 from matching item 10.
    const openRefusedFromLink = (requestId) => {
        setActiveSection("denied_requests");
        if (canReviewRequests) {
            // Approvers still need the pending count right away, not after the refresh timer.
            fetchApprovalRequests();
        }
        fetchDeniedRequests((requests) => {
            if (requestId) {
                // The row's own id first; legacy_id only for links sent before line keys (XPEN-3709).
                const matches = (id) => id === requestId || id?.startsWith(`${requestId}:history:`);
                const matchingRequest = requests.find((request) => matches(request.id))
                    || requests.find((request) => matches(request.legacy_id));
                if (matchingRequest) {
                    setSelectedApprovalRequest(matchingRequest);
                } else {
                    toast.error(translate(
                        "approval_page.request_not_found",
                        "Approval request is no longer pending or is not accessible."
                    ));
                }
            }
            clearRequestIdQueryParam();
        });
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const requestId = params.get("request_id");
        if (params.get("section") === "denied_requests") {
            openRefusedFromLink(requestId);
            return;
        }

        const fetchInitialRequests = canReviewRequests
            ? fetchApprovalRequests
            : fetchMyRequests;
        fetchInitialRequests((requests) => {
            if (!requestId) {
                return;
            }

            const matchingRequest = requests.find((request) => request.id === requestId)
                || requests.find((request) => request.legacy_id === requestId);
            if (matchingRequest) {
                setSelectedApprovalRequest(matchingRequest);
            } else {
                toast.error(translate(
                    "approval_page.request_not_found",
                    "Approval request is no longer pending or is not accessible."
                ));
            }
            clearRequestIdQueryParam();
        });
    }, [])

    useEffect(() => {
        let refreshTimeoutId = null;

        const scheduleRefresh = () => {
            refreshTimeoutId = setTimeout(() => {
                // Reviewers always need fresh pending totals, even when they are
                // viewing one of the requester or history sections.
                if (canReviewRequests) {
                    fetchApprovalRequests();
                }

                // Only refresh the visible secondary section. Inactive sections
                // are fetched immediately when the user selects them.
                if (activeSection === "my_requests") {
                    fetchMyRequests();
                } else if (activeSection === "denied_requests") {
                    fetchDeniedRequests();
                } else if (activeSection === "approved_requests") {
                    fetchApprovedRequests();
                } else if (activeSection === "history") {
                    fetchHistoryRequests();
                }

                scheduleRefresh();
            }, APPROVAL_REFRESH_INTERVAL_MS);
        };

        scheduleRefresh();

        return () => {
            if (refreshTimeoutId !== null) {
                clearTimeout(refreshTimeoutId);
            }
        };
    }, [activeSection, canReviewRequests]);

    const changeTypeFilter = (value) => {
        setTypeFilter(value);
        setSelectedRequestIds([]);
    };

    const displayedRequests = useMemo(() => {
        const sectionRequests = activeSection === "approved_requests"
            ? approvedRequests
            : activeSection === "history"
                ? historyRequests
                : activeSection === "my_requests"
                    ? myRequests
                    : activeSection === "denied_requests" ? deniedRequests : approvalRequests;
        return sectionRequests.filter((request) => matchesRequestTypes(request, typeFilter));
    }, [activeSection, approvedRequests, historyRequests, myRequests, deniedRequests, approvalRequests, typeFilter]);

    return (
        <div className={"card rounded-lg pb-3"} >
            <div className="p-4">
                <div className={" w-100"}>
                    <h2>
                        {translate("approvals", "Approvals")}
                    </h2>
                    <p className={""}>
                        {translate("approval_page.description", "Manage and review invoice and line item approval requests")}
                    </p>

                </div>
                {canReviewRequests && (
                    <div className="d-flex flex-wrap mt-3" style={{gap: 16}}>
                        <SummaryCard
                            label={translate("approval_page.pending_approvals", "Pending Approvals")}
                            value={pendingCount}
                        />
                        <SummaryCard
                            label={translate("approval_page.total_amount_awaiting_review", "Total Amount Awaiting Review")}
                            value={formatMoney(pendingTotal)}
                        />
                    </div>
                )}
                <div
                    className="btn-group mt-4"
                    role="group"
                    aria-label={translate("approval_page.section_buttons", "Approval sections")}
                >
                    {canReviewRequests && (
                        <button
                            type="button"
                            className={`btn ${activeSection === "pending" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => selectSection("pending")}
                        >
                            {translate("approval_page.pending", "Pending")}
                        </button>
                    )}
                    <button
                        type="button"
                        className={`btn ${activeSection === "my_requests" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => selectSection("my_requests")}
                    >
                        {translate("approval_page.my_requests", "My requests")}
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeSection === "denied_requests" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => selectSection("denied_requests")}
                    >
                        {translate("approval_page.denied_requests", "Denied")}
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeSection === "approved_requests" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => selectSection("approved_requests")}
                    >
                        {translate("approval_page.approved_requests", "Approved")}
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeSection === "history" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => selectSection("history")}
                    >
                        {translate("approval_page.history", "History")}
                    </button>
                </div>
                <div className="d-flex flex-wrap align-items-center px-0 py-2 mt-4" style={{rowGap: 8}}>
                    <TypeFilter value={typeFilter} onChange={changeTypeFilter} translate={translate} />
                    {activeSection === "pending" && displayedRequests.length > 0 && (
                        <div className="d-flex align-items-center ml-auto">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm ml-2"
                                disabled={bulkProcessing || selectedCount === 0}
                                onClick={() => submitBulkAction("approve")}
                            >
                                {translate("approve", "Approve")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm ml-2"
                                disabled={bulkProcessing || selectedCount === 0}
                                onClick={openBulkDenialModal}
                            >
                                {translate("deny", translate("disapprove", "Deny"))}
                            </button>
                        </div>
                    )}
                </div>
                {displayedRequests.length > 0 && (
                    <ApprovalRequestTable
                        rows={displayedRequests}
                        mode={activeSection}
                        onRowClick={setSelectedApprovalRequest}
                        selectedRowIds={selectedRequestIds}
                        onRowSelectionChange={updateSelectedRequest}
                        onSelectAllRows={selectAllLoadedRequests}
                    />
                )}
                {displayedRequests.length === 0 && (
                    <div className="text-muted mt-4">
                        {translate("approval_page.no_requests", "No approval requests found.")}
                    </div>
                )}
                {selectedApprovalRequest && (
                    <InvoiceApprovalModal
                        approvalRequest={displayedRequests.find((row) => row.id === selectedApprovalRequest.id) || selectedApprovalRequest}
                        onClose={handleModalClose}
                        onInvoiceUpdated={
                            activeSection === "my_requests"
                                ? fetchMyRequests
                                : activeSection === "denied_requests"
                                    ? fetchDeniedRequests
                                : activeSection === "approved_requests"
                                    ? () => { fetchApprovedRequests(); fetchMyRequests(); fetchApprovalRequests(); }
                                : activeSection === "history"
                                    ? () => { fetchHistoryRequests(); fetchApprovedRequests(); fetchMyRequests(); fetchApprovalRequests(); }
                                : refreshApprovalRequests
                        }
                        allowApprovalActions={activeSection === "pending"}
                        allowResolvedEditing={activeSection === "denied_requests"}
                        allowDeleteAction={activeSection === "denied_requests"}
                        allowResendAction={activeSection === "denied_requests"}
                        allowRequestAnotherApproval={["approved_requests", "history"].includes(activeSection)}
                        allowMoveExpenseAction={activeSection === "denied_requests"}
                        onOpenExpenseReview={activeSection === "denied_requests" ? openExpenseReview : null}
                    />
                )}
                {showBulkDenialModal && (
                    <Modal
                        overflow="visible"
                        title={translate("approval_modal.denial_title", "Denial reason")}
                        cancelAction={() => setShowBulkDenialModal(false)}
                        okAction={confirmBulkDenial}
                        okText={translate("submit", "Submit")}
                        cancelText={translate("cancel", "Cancel")}
                    >
                        <input
                            className="form-control"
                            type="text"
                            autoFocus
                            value={bulkDenialReason}
                            onChange={(event) => setBulkDenialReason(event.target.value)}
                        />
                        {bulkDenialError && <div className="text-danger mt-2">{bulkDenialError}</div>}
                    </Modal>
                )}
            </div>
        </div>
    )
}


export default inject('data')(observer(ApprovalsPage));
